import { WebPushConfig } from "@aitisal/contracts/notification";
import {
  createPushSubscription,
  deletePushSubscription,
  deletePushSubscriptionByToken,
} from "@aitisal/sdk/notification";
import {
  getCurrentFCMToken,
  getPlatform,
  initializeCapacitorPush,
  isNativePlatform,
  removeCapacitorPushListeners,
} from "./capacitorPushNotifications";

// Cache names for SW<->app communication
const PUSH_CONTEXT_CACHE = "push-notification-context";
const PENDING_SUBSCRIPTION_CACHE = "push-subscription-pending";

// Utility to convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = globalThis.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Cache userId for service worker access via Cache API
 */
async function cacheUserId(userId: string): Promise<void> {
  try {
    const cache = await caches.open(PUSH_CONTEXT_CACHE);
    const response = new Response(userId);
    await cache.put("userId", response);
    console.log("Cached userId for service worker access");
  } catch (err) {
    console.warn("Failed to cache userId:", err);
  }
}

/**
 * Get cached userId (for use by service worker or app)
 */
export async function getCachedUserId(): Promise<string | null> {
  try {
    const cache = await caches.open(PUSH_CONTEXT_CACHE);
    const response = await cache.match("userId");
    return response ? await response.text() : null;
  } catch {
    return null;
  }
}

/**
 * Play notification sound - called when push notification arrives while app is open
 */
export async function playNotificationSound(): Promise<void> {
  try {
    const beepSound = document.getElementById("beep-sound") as HTMLAudioElement;
    if (beepSound) {
      beepSound.currentTime = 0;
      await beepSound.play();
    } else {
      const audio = new Audio("/aitisal_order_notification.mp3");
      await audio.play();
    }
  } catch (err) {
    // If blocked due to no user interaction yet, retry after unlock
    if ((err as any)?.name === "NotAllowedError") {
      const onUnlock = async () => {
        try {
          const beepSound = document.getElementById(
            "beep-sound",
          ) as HTMLAudioElement;
          if (beepSound) {
            beepSound.currentTime = 0;
            await beepSound.play();
          } else {
            const audio = new Audio("/aitisal_order_notification.mp3");
            await audio.play();
          }
        } catch (retryErr) {
          console.warn("Retry audio play failed:", retryErr);
        } finally {
          window.removeEventListener("aitisal:audio-unlocked", onUnlock);
        }
      };
      window.addEventListener("aitisal:audio-unlocked", onUnlock);
    } else {
      console.error("Error playing notification sound:", err);
    }
  }
}

/**
 * Setup message listener for service worker communication
 * Handles sound playback requests from service worker
 */
function setupServiceWorkerMessageListener() {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker not supported");
    return;
  }

  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const { type } = event.data;

    switch (type) {
      case "PLAY_NOTIFICATION_SOUND":
        playNotificationSound();
        break;
      default:
        // Ignore unknown message types
        break;
    }
  });

  console.log("Service worker message listener setup complete");
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker not supported");
  }
  return await navigator.serviceWorker.ready;
}

async function subscribeUserToPush(
  forceNew: boolean = false,
): Promise<PushSubscription | null> {
  try {
    const registration = await getRegistration();

    // Check for existing subscription
    const existing = await registration.pushManager.getSubscription();

    if (existing && !forceNew) {
      console.info("User already has an active push subscription");
      return existing;
    }

    // If forcing new subscription, unsubscribe from old one first
    if (existing && forceNew) {
      console.log(
        "Unsubscribing from old subscription before creating new one",
      );
      try {
        await existing.unsubscribe();
      } catch (err) {
        console.warn("Failed to unsubscribe from old subscription:", err);
        // Continue anyway
      }
    }

    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

    if (!vapidKey) {
      console.error(
        "VITE_VAPID_PUBLIC_KEY is missing. Push subscription cannot proceed.",
      );
      throw new Error("VAPID key not configured");
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    console.log("Successfully created new push subscription");
    return subscription;
  } catch (err) {
    console.error("Failed to subscribe to push:", err);
    return null;
  }
}

async function persistWebPushSubscription(
  userId: string,
  subscription: WebPushConfig,
): Promise<boolean> {
  try {
    console.log("Persisting push subscription to server");

    await createPushSubscription({
      userId,
      platform: "web",
      type: "webpush",
      subscriptionObject: subscription,
    });

    console.log("Successfully persisted push subscription");
    return true;
  } catch (err: any) {
    console.error("Failed to persist push subscription:", err);

    // Handle specific error cases
    if (err.response?.status === 409) {
      console.warn("Subscription already exists (conflict)");
      return true;
    }

    return false;
  }
}

/**
 * Initialize native push notifications (Capacitor/FCM)
 */
async function initializeNativePush(
  userId: string,
  onNotificationTap?: (data: any) => void,
): Promise<boolean> {
  try {
    const platform = getPlatform();

    await initializeCapacitorPush(
      async (fcmToken: string) => {
        // Register FCM token with backend
        console.log("FCM token received:", fcmToken);
        try {
          await createPushSubscription({
            userId,
            platform: platform === "ios" ? "ios" : "android",
            type: "fcm",
            subscriptionObject: { token: fcmToken },
          });
          console.log("FCM token registered successfully");
        } catch (err) {
          console.error("Failed to register FCM token:", err);
        }
      },
      (notification) => {
        // Handle notification tap
        console.log("Notification tapped:", notification);
        if (onNotificationTap) {
          onNotificationTap(notification);
        }
      },
    );

    return true;
  } catch (err) {
    console.error("Failed to initialize native push:", err);
    return false;
  }
}

/**
 * Initialize web push notifications
 */
async function initializeWebPush(userId: string): Promise<boolean> {
  try {
    // Setup message listener for SW communication (sound playback)
    setupServiceWorkerMessageListener();

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    // If no subscription and notifications not yet prompted, request permission
    if (!subscription && typeof Notification !== "undefined") {
      if (Notification.permission === "default") {
        // Request permission - this will show the browser prompt
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          subscription = await subscribeUserToPush();
        }
      } else if (Notification.permission === "granted") {
        // Permission already granted, just subscribe
        subscription = await subscribeUserToPush();
      }
    }

    // Persist subscription to server
    if (subscription) {
      await persistWebPushSubscription(
        userId,
        subscription.toJSON() as WebPushConfig,
      );
    }

    return true;
  } catch (err) {
    console.error("Failed to initialize web push:", err);
    return false;
  }
}

/**
 * Initialize push notifications for the current platform
 * Handles permission request, subscription, and server persistence
 * @param userId - Current user's ID
 * @param onNotificationTap - Optional callback when user taps notification (native only)
 */
export async function initializePushNotifications(
  userId: string,
  onNotificationTap?: (data: any) => void,
): Promise<boolean> {
  try {
    // Cache userId for SW access (works for web only, but harmless for native)
    await cacheUserId(userId);

    if (isNativePlatform()) {
      // Native: Capacitor handles permission prompt internally
      return await initializeNativePush(userId, onNotificationTap);
    } else {
      // Web: Setup SW listener and request permission
      return await initializeWebPush(userId);
    }
  } catch (err) {
    console.error("Failed to initialize push notifications:", err);
    return false;
  }
}

/**
 * Clear push notification cache (userId, pending subscriptions)
 */
async function clearPushCache(): Promise<void> {
  try {
    await caches.delete(PUSH_CONTEXT_CACHE);
    await caches.delete(PENDING_SUBSCRIPTION_CACHE);
    console.log("Cleared push notification cache");
  } catch (err) {
    console.warn("Failed to clear push cache:", err);
  }
}

/**
 * Unsubscribe native (FCM) push notifications from server
 */
async function unsubscribeNativePush(): Promise<boolean> {
  try {
    const fcmToken = getCurrentFCMToken();

    if (!fcmToken) {
      console.log("No FCM token to unsubscribe");
      return true;
    }

    // Delete FCM token from server
    // The endpoint format for FCM uses the token as identifier
    try {
      await deletePushSubscriptionByToken(fcmToken);
      console.log("Deleted FCM token from server");
    } catch (err) {
      console.warn("Failed to delete FCM token from server:", err);
    }

    // Remove listeners and reset state
    await removeCapacitorPushListeners();

    return true;
  } catch (err) {
    console.error("Failed to unsubscribe native push:", err);
    return false;
  }
}

/**
 * Unsubscribe web push notifications from server
 */
async function unsubscribeWebPush(): Promise<boolean> {
  try {
    // Clear cached userId to prevent stale data
    await clearPushCache();

    const registration = await getRegistration();
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log("No active web subscription to detach from server");
      return true;
    }

    // Only detach subscription from server; keep local subscription and permission intact
    // This allows quick re-subscription on next sign-in without re-prompting
    try {
      await deletePushSubscription(subscription.endpoint);
      console.log(
        "Detached web push subscription from server (kept local subscription)",
      );
      return true;
    } catch (err) {
      console.warn("Failed to detach web push subscription on server:", err);
      return false;
    }
  } catch (err) {
    console.error("Failed to unsubscribe web push:", err);
    return false;
  }
}

/**
 * Unsubscribe from push notifications and remove from server
 * Called on sign-out to detach subscription from current user
 * Handles both web and native platforms
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    if (isNativePlatform()) {
      return await unsubscribeNativePush();
    } else {
      return await unsubscribeWebPush();
    }
  } catch (err) {
    console.error("Failed to process push unsubscribe:", err);
    return false;
  }
}

/**
 * Check if user has an active push subscription
 */
export async function hasActivePushSubscription(): Promise<boolean> {
  try {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    const registration = await getRegistration();
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (err) {
    console.error("Failed to check for active subscription:", err);
    return false;
  }
}
